import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { User, ApiResponse } from 'types';
import { setAuthToken } from 'utils/storage';
import { mockAuth } from 'mocks/mockServer';

interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken?: string;
}

const useMock = process.env.REACT_APP_USE_MOCK === 'true';

export const authService = {
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    if (useMock) {
      return mockAuth.login(credentials);
    }

    try {
      const response = await axiosInstance.post<ApiResponse<LoginResponse>>(
        API_ENDPOINTS.LOGIN,
        credentials
      );

      // Debug: Log the response from backend
      console.log('🔍 Backend Login Response:', {
        success: response.data.success,
        user: response.data.data?.user,
        hasToken: !!response.data.data?.token
      });

      // API returns wrapped response: { success, message, data }
      if (response.data.success && response.data.data?.token) {
        setAuthToken(response.data.data.token);
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ Login Error:', error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  },

  async logout(): Promise<void> {
    if (useMock) {
      return mockAuth.logout();
    }
    
    try {
      await axiosInstance.post(API_ENDPOINTS.LOGOUT);
    } finally {
      setAuthToken(null);
    }
  },

  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    if (useMock) {
      return { success: true, data: { token: 'mock-token' } };
    }

    try {
      const response = await axiosInstance.post<ApiResponse<{ token: string }>>(
        API_ENDPOINTS.REFRESH_TOKEN
      );

      // API returns wrapped response: { success, message, data }
      if (response.data.success && response.data.data?.token) {
        setAuthToken(response.data.data.token);
      }

      return response.data;
    } catch (error) {
      return {
        success: false,
        error: 'Failed to refresh token'
      };
    }
  }
};